import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import {
  cloneDeep,
  extend,
  find,
  first as firstL,
  forEach,
  get,
  isEmpty,
  isEqual,
  isNumber,
  isObject,
  last,
  map,
  merge,
  unset
} from 'lodash';

import {
  ApiService,
  CountryListService,
  DialogService,
  InputDatastoreService
} from '@nrwl/common-lib';
import { BaseWizardStep } from './base.wizard.step';

@Injectable()
export class BaseWizardService {
  defaultId: any; //eslint-disable-line
  id: any; //eslint-disable-line
  canManageUsersResults: boolean;
  defaultParamsById: any; //eslint-disable-line
  options: any; //eslint-disable-line
  wizardActive: boolean;
  output: any; //eslint-disable-line
  sharedOutput: any; //eslint-disable-line
  steps: any; //eslint-disable-line
  currentStep: any; //eslint-disable-line
  templateModel: any; //eslint-disable-line
  subContentTemplate: any; //eslint-disable-line
  isLoading: boolean;
  reOpenConfirmation: any; //eslint-disable-line
  dirtyFlag: any; //eslint-disable-line
  dialogServiceMsgMap: any; //eslint-disable-line
  classes: any; //eslint-disable-line
  method: any; //eslint-disable-line
  finishingStep: BaseWizardStep;
  errors: any; //eslint-disable-line
  params: any; //eslint-disable-line

  reOpen: any; //eslint-disable-line
  onSuccess: any; //eslint-disable-line
  onFail: any; //eslint-disable-line
  resetToDefault: any; //eslint-disable-line
  subContentVisibility: any; //eslint-disable-line
  statusText: any; //eslint-disable-line

  lastSubmittedOutput;
  stepClass: any; //eslint-disable-line

  // properties initialized from wizardConfig.valuesFixed
  dataUrl: string;
  updateUrl: string;
  dimension: string;
  finishStep: string;
  format: string;
  /* eslint-disable-next-line */
  formatData: any; // eg {alias: "", service: {…}, originator: "", notifications: "", meta_data: "", …}
  hideFinishBtn: boolean;
  noResponseError: boolean;
  noSteps: boolean;
  openAnother: boolean;
  showSteps: boolean;
  stepsInfo: string;
  title: string;
  type: string;
  editMode = false;
  dataSaved = false;
  requestFailed = false;
  errorMessage: string;
  errorDescription: string;
  successMessage: string;
  apiService: ApiService;
  dialogService: DialogService;
  isDarkTheme = false;
  // plenty of code is written to call toggleSubContent directly on .base.wizard.step instance, representing subcontent page.
  // to avoid extensive refactoring right now, I simply register a listener/which will be notified about toggle event, being propagated up.
  toggleSubContentListener: (callback: any) => void; //eslint-disable-line

  constructor(
    apiService: ApiService,
    private readonly inputDatastoreService: InputDatastoreService,
    private readonly countryListService: CountryListService,
    dialogService: DialogService
  ) {
    this.apiService = apiService;
    this.stepClass = BaseWizardStep;
    this.dialogService = dialogService;
    this.defaultId = 'wizardService';
    this.id = null;
    this.canManageUsersResults = false;
    this.defaultParamsById = {};
    this.options = {
      visible: false
    };
    this.wizardActive = false;
    this.output = {};
    this.sharedOutput = {};
    this.steps = [];
    this.currentStep = null;
    this.templateModel = null;
    this.subContentTemplate = null;
    this.isLoading = false;
    this.dirtyFlag = false;
    this.reOpenConfirmation = false;
    this.dataSaved = false;
    this.requestFailed = false;

    this.reOpen = () => {};

    this.dialogServiceMsgMap = {};
  }

  /* eslint-disable-next-line */
  _getId(wizardId: string): any {
    return `${this.defaultId}.${wizardId}`;
  }

  //------------------------------------------------ Init config/data ----------------------------------------------------

  /* eslint-disable-next-line */
  registerToggleListener(callback: any): void {
    this.toggleSubContentListener = callback;
  }

  // some fancy wizards may decide to show 'Exit' instead of 'Close' in case an attempt to 'create' results has failed.
  showExitInsteadOfClose(): boolean {
    return false;
  }

  /* eslint-disable-next-line */
  _setSteps(wizardConfig: any): any {
    //Fill fixed values
    merge(this, wizardConfig.valuesFixed);
    this.classes = wizardConfig.classes;
    this.method = this.method || 'post';
    this.steps = map(wizardConfig.steps, step => {
      const stepConfig = extend({}, step, {
        id: [this.id, step.id].join('.')
      });

      return new this.stepClass(this.apiService, stepConfig, this);
    });

    if (wizardConfig.finishingStep) {
      const stepConfig = extend({}, wizardConfig.finishingStep, {
        id: [this.id, wizardConfig.finishingStep.id].join('.')
      });
      this.finishingStep = new this.stepClass(this.apiService, stepConfig, this);
    }
  }

  /* eslint-disable-next-line */
  _initStepData(step: any, target: any, defaultParamsById: any): void {
    if (step.config.def.dataUrl) {
      const subPath = step.config.def.subPath;
      const key = step.config.def.key;
      const method = step.config.def.method ? step.config.def.method : this.method;
      const callback = () => {
        this._setDefaultParams(step, target.output, defaultParamsById, true);
        target.isLoading = false;
      };
      step._requestData(subPath, callback, key, method);
    } else {
      this._setDefaultParams(step, target.output, defaultParamsById);
    }
  }

  /* eslint-disable-next-line */
  _setDefaultParams(step: any, targetOutput: any, defaultParamsById = {}, load?: any): void {
    forEach(step.output, (item, key) => {
      if (
        isNumber(defaultParamsById[key]) ||
        (!isEmpty(defaultParamsById[key]) &&
          isEmpty(item) &&
          (!step.config.def.filterDataSource || key !== step.config.def.filterDataSource.hide))
      ) {
        targetOutput[key] = step._setOutputBySendParam(cloneDeep(defaultParamsById[key]));
      }
    });
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  openWizard(
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    wizardId: any,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    wizardConfig: any,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {}
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): any {
    this.isDarkTheme = this.inputDatastoreService.isDarkTheme();
    const dataParams = {
      wizardId,
      wizardConfig,
      defaultParams: defaultParamsById,
      onSuccess,
      onFail
    };
    if (this._cancelOpenWizard(dataParams)) {
      return;
    }

    this.onSuccess = onSuccess;
    this.onFail = onFail;
    this.reOpen = () => {
      this.id = null;
      this.openWizard(wizardId, wizardConfig, defaultParamsById, onSuccess, onFail);
    };
    this.reOpenConfirmation = false;

    this.id = this._getId(wizardId);
    // It is important to re-set data, possible left-over from previous run of wizard
    this.output = {};
    this.sharedOutput = {};
    this.hideFinishBtn = false;
    this.resetToDefault = false;
    this.toggleSubContent(false);
    this.formatData = null;
    this.openAnother = null;
    this.dataUrl = wizardConfig.valuesFixed.dataUrl;
    this.updateUrl = wizardConfig.valuesFixed.updateUrl;
    this.defaultParamsById = defaultParamsById;
    this.dataSaved = false;
    this._setSteps(wizardConfig);

    this.currentStep = firstL(this.steps);
    if (!isEmpty(this.currentStep)) {
      this._initStepData(this.currentStep, this.currentStep, this.defaultParamsById);

      if (
        this.currentStep.config.def.subContent &&
        this.currentStep.config.def.subContent.def.dataUrl
      ) {
        this._initStepData(
          this.currentStep.subContent,
          this.currentStep,
          this.defaultParamsById.subContent
        );
      }
    }
    this.wizardActive = true;
    this.options.visible = true;
  }

  /* eslint-disable-next-line */
  _cancelOpenWizard(params: any): boolean {
    const id = this._getId(params.wizardId);
    if (
      this.id !== id &&
      this.dirtyFlag &&
      this.wizardActive &&
      !this.reOpenConfirmation &&
      this.dialogServiceMsgMap[this.id] !== undefined
    ) {
      this.askForConfirmation(this.reOpenWizardAfterConfirmation, params);
      return true;
    }

    return !!(
      this.id === id &&
      this.options.visible &&
      isEqual(params.defaultParams, this.defaultParamsById)
    );
  }

  /* eslint-disable-next-line */
  setNextStep(previousStep?: any): void {
    this.currentStep = this.steps[this.currentStep.config.index + 1];
    this.currentStep.toggleSubContent(false);
  }

  nextStep(): void {
    this.currentStep.onNext();
    this.dirtyFlag = true;
    this.setNextStep();
    //Selects default output value by id
    if (this.editMode || this.currentStep.config.def.dataUrl) {
      this._initStepData(this.currentStep, this.currentStep, this.defaultParamsById);
    }

    this._initSupContents(this.currentStep);
  }

  /* eslint-disable-next-line */
  goToStep(stepName: any): void {
    this.currentStep = find(this.steps, step => {
      if (step.id.includes(stepName)) {
        return step;
      }
    });
    this._initStepData(this.currentStep, this.currentStep, this.defaultParamsById);
    this.currentStep.toggleSubContent(false);
  }

  /* eslint-disable-next-line */
  _initSupContents(currentStep: any): void {
    if (get(currentStep, 'config.def.subContent.def.dataUrl', false)) {
      this._initStepData(currentStep.subContent, currentStep, this.defaultParamsById.subContent);
      this._initSupContents(currentStep.subContent);
    }
  }

  _setPreviousStep(): void {
    this.currentStep = this.steps[this.currentStep.config.index - 1];
  }

  previousStep(): void {
    this.currentStep.onBack();

    this._setPreviousStep();

    this.currentStep.selected = false;
    this.currentStep.selectedTab = 0;
    // it will be bound to form on new page, if present
    this.currentStep.form = null;
    this.currentStep.toggleSubContent(false);
    this.errors = [];
  }

  isLastStep(): boolean {
    return (
      this.currentStep && this.currentStep.config.index >= last<any>(this.steps).config.index //eslint-disable-line
    );
  }

  isFinishingStep(): boolean {
    return (
      this.currentStep && this.currentStep.config.index > last<any>(this.steps).config.index //eslint-disable-line
    );
  }

  toggleClose(callBack = () => {}): void {
    if (this.dirtyFlag) {
      this.askForConfirmation(this.reOpenWizardAfterConfirmation, {
        close: true,
        callBack
      });
    } else {
      this.id = null;
      this.defaultParamsById = {};
      this.toggleSubContent(false);
      this.options.visible = false;
      this.formatData = null;
      this.method = null;
      this.openAnother = null;
      unset(this, 'noResponseError');
      this.errors = [];
      this.finishingStep = null;
      this.reOpen = () => {};
      this.hideFinishBtn = false;
      this.wizardActive = false;
      this.dirtyFlag = false;
      this.editMode = false;
      callBack();
    }
  }

  /* eslint-disable-next-line */
  toggleSubContent(value?: any, templateModel?: any): void {
    this.subContentVisibility = value !== undefined ? value : !this.subContentVisibility;
    if (!isEmpty(templateModel)) {
      this.templateModel = templateModel;
      this.subContentTemplate = templateModel.config.def.template;
    }
    if (this.toggleSubContentListener) {
      this.toggleSubContentListener(this.subContentVisibility);
    }
  }

  //This method will be invoked from dialog service after user confirmation
  /* eslint-disable-next-line */
  reOpenWizardAfterConfirmation(service: any, params: any): void {
    if (params !== null) {
      service.reOpenConfirmation = true;
      service.dirtyFlag = false;
      service.toggleClose();
      if (!params.close) {
        service.openWizard(
          params.wizardId,
          params.wizardConfig,
          params.defaultParams,
          params.onSuccess
        );
      }
      if (params.callBack) {
        params.callBack();
      }
    } else {
      service._saveWizardData();
    }
  }

  /* eslint-disable-next-line */
  askForConfirmation(onConfirm: any, fnParams: any): void {
    this.params = {
      id: this.id,
      callBackService: this,
      callBackFnParams: fnParams,
      callBackFuntion: onConfirm
    };
    if (this.dialogService) {
      this.dialogService.showDialog(this.id, 'body', this.params);
    }
  }

  onFinish(): void {
    this._saveWizardData();
  }

  onFinishOpenNew(): void {
    // default implementation does nothing
  }

  /* eslint-disable-next-line */
  _saveWizardData(openOnFinish = false): any {
    //Save last step data
    this.currentStep.onNext();
    this.dirtyFlag = false;
    const output = this._getFormatOutput();
    return this._saveRequest(output, openOnFinish);
  }

  /**
   * in case you want to simulate specific response from backend + delay,
   * you can try this:
   * - comment out original 'const request = ...apiService...'
   * - insert this:
   * import { of } from "rxjs";
   * import { delay } from "rxjs/internal/operators";
   * const request = of({result: true, success: true}).pipe(delay(10000));
   */

  /* eslint-disable-next-line */
  _saveRequest(output: any, openOnFinish?: boolean): any {
    if (output.alias) {
      output.alias = output.alias.trim();
    }
    this.isLoading = true;
    this.requestFailed = false;
    this.errorMessage = null;
    this.errorDescription = null;
    /* eslint-disable-next-line */
    const body = this.dataUrl.includes('vibe') ? [output] : output;
    if (body.curlUrl) {
      body.curlUrl = btoa(body.curlUrl);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const request = this.apiService[this.method + '$']<any>(this.dataUrl, body).pipe(first());
    // we want to be able to notify the top level angular component about end of request.
    // if we return 'request' itself, then there is no guarantee which subscriber will run first (hence
    // there is a risk that your listener/angular component will be notified sooner than base.wizard.service
    // updates inner state variables). Hence a separate Subject object.
    /* eslint-disable-next-line */
    const responseProcessed: Subject<any> = new Subject();
    request.subscribe(
      response => {
        //this.inputDatastoreService.broadcastUpdate(this.inputDatastoreService.getInputs(), false);
        if (!this.finishingStep) {
          this.onSuccess(response.success, this.output);
        }
        this.dataSaved = true;
        this.isLoading = false;
        // notice the simplicity of emitted value! The reason: subscribers must use base.wizard.service state variables
        // (such as dataSaved, requestFailed, isLoading, etc) to figure out what is the outcome of the call.
        responseProcessed.next(response);
        responseProcessed.complete();
      },
      error => {
        this.isLoading = false;
        this.requestFailed = true;
        this._handleErrorResponse(error, this.onFail);
        // see remark above on reasons for emitting a plain {} object.
        responseProcessed.next({});
        responseProcessed.complete();
      },
      () => {}
    );
    return responseProcessed.pipe(first());
  }

  /**
   * The final step of wizard/creating data in backend involves multiple asynchronous calls
   * to backend/any of them can fail.
   * This is a shared helper method to process failed calls.
   * @param {*} response Backend response
   * @param {*} onFail A special function to be invoked. An optional parameter. Example of use - dashboard will be notified
   * if wizard fails to create new data - giving it a chance to pop up some message to end user.
   *
   */

  /* eslint-disable-next-line */
  _handleErrorResponse(response: any, onFail: any): void {
    this.isLoading = false;
    if (response && response.error && get(response, 'error.errors', false)) {
      if (get(response, 'error.errors', false) instanceof Array) {
        this.errorMessage = response.error.errors[0] || response.error.errors[1];
        this.errorDescription = response.error.errors[0] || response.error.errors[1];
      }
    } else if (response && response.error && get(response, 'error.message', false)) {
      this.errorMessage = response.error.message;
      this.errorDescription = response.error.message;
    } else if (
      response &&
      response.error &&
      (typeof response.error === 'string' || response.error instanceof String)
    ) {
      // analyze given string. if it contains html-formatted text, it is most likely to present a generic error page
      // from backend infrastructure. Default to 'unknown' error in this case/do not show returned HTML.
      if (!/.*<\/html>.*/.test(response.error)) {
        this.errorMessage = response.error;
        this.errorDescription = response.error;
      } else {
        this.errorMessage = 'Reason unknown';
        this.errorDescription = 'Reason unknown';
      }
    } else if (
      response?.error?.path === '/logging/v1/api/output' &&
      response?.error?.status === 409
    ) {
      this.errorMessage =
        'ERROR: A syslog configuration with this identifier already exists. Please choose another.';
      this.errorDescription = '';
    } else {
      this.errorMessage = 'Reason unknown';
      this.errorDescription = 'Reason unknown';
    }
    if (onFail) {
      onFail(response);
    }
  }

  //----------------------------------------------- Format output data ---------------------------------------------------

  /* eslint-disable-next-line */
  _getFormatOutput(output = this.output, format = this.formatData): any {
    const formattedOutput = {};
    forEach(format, (param, key) => {
      if (!isEmpty(param)) {
        formattedOutput[key] = this._getFormatOutputItem(output[key], param);
      } else {
        formattedOutput[key] = output[key];
      }
    });
    return formattedOutput;
  }

  /* eslint-disable-next-line */
  _getFormatOutputItem(output: any, format: any): any {
    let newOutput = {};
    if (format.type === 'array') {
      newOutput = map(output, function (item) {
        let newItem = {};

        if (format.param) {
          newItem = item[format.param];
        } else {
          forEach(format.params, function (key) {
            newItem[key] = item[key];
          });
        }
        return newItem;
      });
    } else if (format.type === 'object') {
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      forEach(format.params, function (item: any) {
        //TODO fix it
        if (isObject(item)) {
          const values = output[item.value];
          forEach(values, value => unset(value, 'selected'));
          newOutput[item.key] = values;
        } else {
          newOutput[item] = output[item];
        }
      });
    } else if (format.type === 'key:value') {
      newOutput = map(output, item => {
        return `${item.key}:${item.value}`;
      });
    } else if (format.param) {
      newOutput = output[format.param];
    } else {
      newOutput = format;
    }
    return newOutput;
  }

  /* eslint-disable-next-line */
  _setOutputForEditMode(defaultParamsById: any): void {
    forEach(defaultParamsById, (param, key) => {
      this.output[key] = defaultParamsById[key];
    });
  }

  //-------------------------------------------------Helpers------------------------------------------------------------

  /* eslint-disable-next-line */
  itemExists(item: any, list: any): boolean {
    return list.indexOf(item) > -1;
  }

  // toggleItem(item, list, form, fname) {
  //   const idx = list.indexOf(item);
  //   if (idx > -1) {
  //     list.splice(idx, 1);
  //   } else {
  //     list.push(item);
  //   }
  //   form[fname] = list.join(",");
  // }

  /* eslint-disable-next-line */
  _clearSkippedPageOutput(skippedStep: any): void {
    // right now, not much to do in service itself. just ask the page to clean itself (and its traces from parentOutput)
    skippedStep.clearSkippedPageOutput();
  }
}
